import clsx from "clsx";
import { GreatDeal } from "./greatDeal";
import { LoadingCircle } from "../common";
import { DetailsHome } from "./detailsHome";
import { PiArrowLeft } from "react-icons/pi";
import { BrowseSlider } from "./browseSlider";
import { fetchGraph } from "../../services/graphs";
import { PropertyDetails } from "./propertyDetails";
import { useEffect, useRef, useState } from "react";
import { PropertyAnalysis } from "./propertyAnalysis";
import { AboutMarket } from "./AboutMarket/aboutMarket";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMapStore } from "../../stores/mapCordinations";
import { BrowseListingSlider } from "./browseListingSlider";
import type { ListingGraphs } from "../../interfaces/graphs";
import { SimilarActiveListings } from "./similarActiveListings";
import type { ListingDetailed } from "../../interfaces/listing";
import { fetchBrowseListing } from "../../services/listingDetailed";
import { NeighbourhoodInformation } from "./neighbourhoodInformation";
import { comparableListings } from "../../services/comparableListings";
import { BrowseListingLayout } from "../../layouts/browseListingLayout";
import type { ComparablesData } from "../../interfaces/comparablesInterface";

const BrowseListingView = ({
  listingId: propListingId,
}: {
  listingId?: string;
}) => {
  const { listingId: paramListingId } = useParams<{ listingId: string }>();
  const listingId = paramListingId || propListingId;
  const hasFetchedData = useRef(false);

  const [overlayOpen, setOverlayOpen] = useState<boolean>(false);
  const {
    showProperty,
    setShowProperty,
    setShowSlider,
    showSlider,
    fullWidthProperty,
    setHandleNavigate,
    handleNavigate,
  } = useMapStore();
  const [activeIndex, setActiveIndex] = useState(0);
  const [listing, setListing] = useState<ListingDetailed | null>(null);
  const [statistics, setStatistics] = useState<ListingGraphs | null>(null);
  const navigate = useNavigate();
  const [comparableData, setComparableData] = useState<ComparablesData | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [isMapView, setIsMapView] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const searchParms = new URLSearchParams(window.location.search);

    if (searchParms.has("zlid") && showProperty === false) {
      const zlid = searchParms.get("zlid");
      if (zlid) {
        setShowProperty(() => true);
        navigate(`listings/${zlid}`);
      }
    }
  }, []);
  useEffect(() => {
    if (location.pathname.startsWith("/listings/")) {
      const zlid = location.pathname.split("/listings/")[1];
      if (zlid) {
        const newUrl = `${window.location.origin}/?zlid=${zlid}`;
        window.history.replaceState({}, "", newUrl);
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/") {
      setIsMapView(true);
    } else {
      setIsMapView(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (listingId && showProperty) {
      const currentUrl = window.location.href;

      if (currentUrl.includes("?zlid=")) {
        const zlid = new URLSearchParams(window.location.search).get("zlid");

        if (zlid) {
          const newUrl = `${window.location.origin}/?zlid=${zlid}`;

          window.history.replaceState({}, "", newUrl);
        }
      }
      const baseUrl = `${window.location.origin}`;
      const newUrl = `${baseUrl}?zlid=${listingId}`;
      window.history.pushState({}, "", newUrl);
    }
  }, [listingId]);

  useEffect(() => {
    if (showProperty === true) {
      setHandleNavigate(() => false);
    }
  }, [showProperty]);

  useEffect(() => {
    const searchParms = new URLSearchParams(window.location.search);

    if (isMapView && !showProperty && searchParms.has("zlid")) {
      const newUrl = `${window.location.origin}`;
      window.history.replaceState({}, "", newUrl);
    }
  }, [showProperty, isMapView]);

  useEffect(() => {
    setListing(null);
    setStatistics(null);
    hasFetchedData.current = false;
  }, [listingId]);

  const handleImageClick = (index: number) => {
    setActiveIndex(index);
    setShowSlider(true);
  };

  useEffect(() => {
    const fetchListing = async () => {
      if (hasFetchedData.current || !listingId) {
        return;
      }
      try {
        setLoading(true);
        const listingDetails = await fetchBrowseListing(listingId);
        setLoading(false);

        setListing(listingDetails?.listing);
      } catch (error) {
        console.error("Failed to fetch listing details:", error);
      }
    };
    fetchListing();
  }, [listingId]);

  useEffect(() => {
    (async () => {
      if (hasFetchedData.current || !listingId) {
        return;
      }
      try {
        const response = await comparableListings(listingId);
        setComparableData(response);
      } catch (error) {
        console.error("Error fetching comparables data:", error);
      } finally {
        // setLoading(false);
      }
    })();
  }, [listingId]);

  useEffect(() => {
    const fetchStatistics = async () => {
      if (hasFetchedData.current || !listingId) {
        return;
      }
      try {
        const response = await fetchGraph(listingId);

        setStatistics(response);
      } catch (error) {
        console.error("Failed to fetch Statistics data", error);
      }
    };

    fetchStatistics();
  }, [listingId]);

  return (
    <BrowseListingLayout
      pb="40px"
      customBg={!loading}
      isShowProperty={showProperty}
      fullWidthProperty={fullWidthProperty}
    >
      {loading && (
        <div className="absolute inset-0 h-screen flex flex-col items-center justify-center z-10">
          <LoadingCircle />
        </div>
      )}
      <div
        className={clsx(
          "w-full relative",
          showProperty ? "pt-0" : "pt-20",
          !isMapView && "pt-20"
        )}
      >
        {(!showProperty || !isMapView) && (
          <div
            className="flex items-center space-x-3 cursor-pointer mb-10"
            onClick={() => {
              setShowProperty(() => false);
              window.history.replaceState({}, "", "/");
              navigate("/", { replace: true });
            }}
          >
            <PiArrowLeft className="text-xl" />
            <span className="text-z-shark">Back</span>
          </div>
        )}
        {!loading && (
          <>
            <BrowseListingSlider
              listing={listing}
              setOverlayOpen={setOverlayOpen}
            />
            <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-7 xl:gap-12 mt-16">
              <div>
                <DetailsHome listing={listing} statistics={statistics} />
                <PropertyDetails statistics={statistics} listing={listing} />
                <PropertyAnalysis
                  comparableData={comparableData}
                  listing={listing}
                  statistics={statistics}
                />
                <NeighbourhoodInformation listing={listing} />
                {statistics && <AboutMarket statistics={statistics} />}
              </div>
              <GreatDeal listing={listing} />
            </div>
          </>
        )}
        {!loading && comparableData?.similar && (
          <SimilarActiveListings comparableData={comparableData} />
        )}

        {!loading && overlayOpen && (
          <div
            className={clsx(
              "fixed inset-0 bg-white w-full h-full z-50 flex items-center justify-center",
              showProperty && "pt-[120px]"
            )}
          >
            <div className="pb-10 w-full h-full overflow-auto">
              <div className="flex items-center justify-between mx-auto sticky top-0 h-24 bg-white">
                <div className="max-w-[70%] flex items-center justify-between w-full mx-auto">
                  <div
                    className="flex items-center space-x-3 cursor-pointer"
                    onClick={() => setOverlayOpen(false)}
                    onKeyDown={() => setOverlayOpen(false)}
                  >
                    <PiArrowLeft className="text-xl" />
                    <span className="text-z-shark font-bold">Back</span>
                  </div>
                  <p className="mt-2 text-z-shark text-2xl">
                    {listing?.address}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 max-w-screen-lg mx-auto mt-8">
                {listing?.images?.map((image, index) => (
                  // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
                  <img
                    onClick={() => handleImageClick(index)}
                    key={index}
                    src={image}
                    alt={`Overlay Image ${index + 1}`}
                    className={`rounded-lg w-full object-cover cursor-pointer ${
                      index === 0
                        ? "col-span-2 row-span-2 h-[500px]"
                        : "h-[300px]"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {showSlider && listing && (
        <BrowseSlider
          listing={listing}
          setShowSlider={setShowSlider}
          activeIndex={activeIndex}
        />
      )}
    </BrowseListingLayout>
  );
};

export { BrowseListingView };
